
import { useState, useEffect, lazy, Suspense } from "react";
import { Film, ArrowRight } from "lucide-react";
import { ContentSection } from "./SectionContainers";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";

// Lazy load the content carousel component
const ContentCarousel = lazy(() => import("./ContentCarousel"));
const ChannelCard = lazy(() => import("./ChannelCard"));

interface Channel {
  id: string | number;
  name: string;
  thumbnailUrl?: string;
  logoLight?: string;
  logoDark?: string;
  logo_light?: string; // Support both naming conventions
  logo_dark?: string;  // Support both naming conventions
  logo?: string;       // Fallback for legacy data
}

interface FeaturedChannelsProps {
  channels: Channel[];
}

export const FeaturedChannels = ({ channels }: FeaturedChannelsProps) => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    // Use Intersection Observer to detect when the component enters viewport
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    
    // Get current reference to the container
    const sectionRef = document.getElementById('featured-channels-section');
    if (sectionRef) {
      observer.observe(sectionRef);
    }
    
    return () => {
      if (sectionRef) {
        observer.unobserve(sectionRef);
      }
    };
  }, []);
  
  if (channels.length === 0) {
    return null;
  }
  
  const sectionTitle = t('explore.channels.title');
  const sectionDescription = t('explore.channels.description');
  
  const viewAllButton = (
    <Button variant="outline" size="sm" asChild className="whitespace-nowrap">
      <Link to="/channels" className="flex items-center">
        {t("common.viewAll")} <ArrowRight className="ml-2 h-4 w-4" />
      </Link>
    </Button>
  );
  
  return (
    <div id="featured-channels-section">
      <ContentSection 
        title={sectionTitle}
        description={sectionDescription}
        icon={Film}
        viewAllText={viewAllButton}
      >
        {isVisible ? (
          <Suspense fallback={
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <Skeleton key={i} className="h-48 rounded-lg" />
              ))}
            </div>
          }>
            <ContentCarousel 
              items={channels}
              renderItem={(channel: Channel) => <ChannelCard channel={channel} />}
              viewAllLink="/channels"
            />
          </Suspense>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
                <Skeleton key={i} className="h-48 rounded-lg" />
              ))}
            </div>
        )}
      </ContentSection>
    </div>
  );
};

// Make sure to add a proper default export
export default FeaturedChannels;
